import { Stack } from '@fluentui/react';
import { FluentProvider, Spinner, webLightTheme } from '@fluentui/react-components';
import { toJS } from 'mobx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { IFormEditParams } from '../../../components/Form';
import { useDebouncedValue } from '../../../hooks';
import { EmptyLayout } from '../../../layout/EmptyLayout/EmptyLayout';
import { injectIntlWithObserver, IRouterWithIntl } from '../../../utils';
import { addToMyReports, changeReport, onChangeReportId } from '../@data/actions';
import { TythonColumnsEnum } from '../@data/store/schema/ColumnsEnum';
import { getPageStore, getPublisherId } from '../@data/store/store';
import { IPubCenterMetadataElement, PubCenterReportingDataResponse } from '../DashboardCards/@data/store/schema';
import { constructPubCenterReportingRequestBody } from '../DashboardCards/ReportCard/ReportCard.helpers';
import { ChartGrain } from '../DashboardCards/ReportCard/ReportCard.types';
import { ICustomCalenderDateRange, RangeType } from '../components/CustomCalender/CustomCalender.types';
import { ReportDataDelayMessageBar } from '../components/DataDelayMessageBar/DataDelayMessageBar';
import { IReportItem } from '../components/LeftHandNavigation/LeftHandNavigation.types';
import {
  useDeleteReport,
  useEditReport,
  usePostReport,
  usePubCenterReportMetadaDimensions,
  usePubCenterReportMetadata,
  usePubCenterReportQuery,
  usePubCenterReportTypes,
  useSavedReports,
} from '../hooks/useReportQuery';
import { DataActionComponent } from './Components/DataActionComponent';
import { PerformanceChartComponent } from './Components/PerformanceChartComponent';
import { ReportActionComponent } from './Components/ReportActionComponent';
import { useReportDetailPageStyles } from './DetailPage.styles';
import {
  AdTypesFilter,
  aggregateByField,
  aggregateReportData,
  generateNewName,
  getIsPremadeReport,
  reportTypeDimensionConfig,
  reportTypesWithNoChart,
  reportTypesWithNoGranularity,
} from './utils/utils';

export type IDetailPageProps = IRouterWithIntl<IFormEditParams>;

// --- Helper: Get dimension requests for a report type ---
function getDimensionRequestsForReportType(reportType: string) {
  return reportTypeDimensionConfig[reportType.toLowerCase()] || [];
}

// --- Helper: Fetch all dimensions for a report type ---
function useReportTypeDimensions(publisherId: string, reportType: string) {
  const dimensionRequests = useMemo(() => getDimensionRequestsForReportType(reportType), [reportType]);

  // Always call the hook 5 times (max needed), fill with undefined if not used
  const dimen1 = dimensionRequests[0];
  const dimen2 = dimensionRequests[1];
  const dimen3 = dimensionRequests[2];
  const dimen4 = dimensionRequests[3];
  const dimen5 = dimensionRequests[4];

  const dataDimen1 = usePubCenterReportMetadaDimensions(publisherId, dimen1?.filter, dimen1?.hierarchy);
  const dataDimen2 = usePubCenterReportMetadaDimensions(publisherId, dimen2?.filter, dimen2?.hierarchy);
  const dataDimen3 = usePubCenterReportMetadaDimensions(publisherId, dimen3?.filter, dimen3?.hierarchy);
  const dataDimen4 = usePubCenterReportMetadaDimensions(publisherId, dimen4?.filter, dimen4?.hierarchy);
  const dataDimen5 = usePubCenterReportMetadaDimensions(publisherId, dimen5?.filter, dimen5?.hierarchy);

  // Build filtersAvailable object
  const filtersAvailable: Record<string, IPubCenterMetadataElement[]> = {};
  if (dimen1) {
    filtersAvailable[dimen1.filterDisplayName] = dataDimen1?.data ?? [];
  }
  if (dimen2) {
    filtersAvailable[dimen2.filterDisplayName] = dataDimen2?.data ?? [];
  }
  if (dimen3) {
    filtersAvailable[dimen3.filterDisplayName] = dataDimen3?.data ?? [];
  }
  if (dimen4) {
    filtersAvailable[dimen4.filterDisplayName] = dataDimen4?.data ?? [];
  }
  if (dimen5) {
    filtersAvailable[dimen5.filterDisplayName] = dataDimen5?.data ?? [];
  }

  if (reportType === 'TypeTag') {
    filtersAvailable['Ad Type'] = AdTypesFilter;
  }

  return filtersAvailable;
}

export const DetailPage = injectIntlWithObserver<IDetailPageProps>((props: IDetailPageProps) => {
  const { reports: mobxReports } = getPageStore();

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 500);
  const [hideChart, setHideChart] = useState(false);

  const { mutate: postReport, isLoading: isPostLoading } = usePostReport();
  const { mutate: deleteReport, isLoading: isDeleteLoading } = useDeleteReport();
  const { mutate: editReport, isLoading: isEditLoading } = useEditReport();

  const publisherId = getPublisherId();
  const performanceReportCardRef = useRef<HTMLDivElement>(null);
  const classNames = useReportDetailPageStyles();
  const reports = toJS(mobxReports);
  const { reportId } = props.match.params;

  const currentReport = useMemo(
    () =>
      reports
        ?.find((section) => section.items.some((item) => item.id.toLowerCase() === reportId?.toLowerCase()))
        ?.items.find((item) => item.id.toLowerCase() === reportId?.toLowerCase()),
    [reportId, reports]
  );

  const reportType = currentReport?.reportType ?? 'account';
  const dateRange = currentReport?.dateRange ?? { startDate: new Date(), endDate: new Date() };
  const reportName = useMemo(
    () =>
      `${currentReport?.reportType} ${dateRange.startDate.getFullYear()}-${
        dateRange.startDate.getMonth() + 1
      }-${dateRange.startDate.getDate()} to ${dateRange.endDate.getFullYear()}-${
        dateRange.endDate.getMonth() + 1
      }-${dateRange.endDate.getDate()}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateRange, currentReport?.reportType]
  );

  const { data: metadata, isLoading: isMetadataLoading } = usePubCenterReportMetadata(reportType, false);
  const { data: savedReports, isLoading: isSavedReportsLoading } = useSavedReports();
  const { isLoading: isReportTypesLoading } = usePubCenterReportTypes();

  const grains = useMemo(() => metadata?.grains.map((item) => item.displayName.toUpperCase()), [metadata]);
  const filtersAvailable = useReportTypeDimensions(publisherId, reportType);
  const { AdDistribution, ...rest } = currentReport?.filters ?? {};

  const adDistributionFilter = AdDistribution;
  const restFilters = rest;

  const isSnapshotReport = reportType?.toLowerCase().includes('snapshot') || false;
  const isPremadeReport = getIsPremadeReport(currentReport, toJS(reports));
  const isNewReport = !savedReports?.some((report) => report.id === currentReport?.id) && !isPremadeReport;
  const isRangeTypeCustom = currentReport?.rangeType === RangeType.Custom;

  if (!currentReport && !isSavedReportsLoading && !isReportTypesLoading && !isDeleteLoading && !isPostLoading && reports.length > 1) {
    onChangeReportId(undefined, props.history);
  }
  // --- Request body for report data ---
  const requestBody = useMemo(
    () =>
      constructPubCenterReportingRequestBody(
        reportName,
        reportType,
        dateRange,
        reportType && reportTypesWithNoGranularity.has(reportType) ? ChartGrain.NONE : currentReport?.grain ?? ChartGrain.DAILY,
        restFilters,
        // allColumns[reportType],
        currentReport?.columns || [],
        adDistributionFilter && adDistributionFilter.length >= 1 ? adDistributionFilter[0] : 'NativeAndPaidSearch'
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportType, dateRange, currentReport?.grain, restFilters, currentReport?.columns, adDistributionFilter, isMetadataLoading]
  );

  // --- Fetch report data ---
  const { data: reportData, isLoading: isReportDataLoading } = usePubCenterReportQuery(requestBody, !isMetadataLoading && !!currentReport);

  // --- Request body for Summary data ---
  const metricPickerRequestBody = useMemo(
    () =>
      constructPubCenterReportingRequestBody(
        reportName,
        reportType ?? 'Account',
        dateRange,
        ChartGrain.NONE,
        restFilters,
        metadata?.metrics.map((metric) => metric.filterKey),
        adDistributionFilter && adDistributionFilter.length >= 1 ? adDistributionFilter[0] : 'NativeAndPaidSearch'
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportType, dateRange, isMetadataLoading, restFilters]
  );

  // --- Fetch report data ---
  const { data: summaryMetricReportData } = usePubCenterReportQuery(
    metricPickerRequestBody,
    reportType && reportTypesWithNoChart.has(reportType) ? false : !isMetadataLoading
  );

  // --- Request body for aggregated data  for Chart ---
  const aggregatedDataRequestBody = useMemo(
    () =>
      constructPubCenterReportingRequestBody(
        reportName,
        reportType ?? 'Account',
        dateRange,
        reportType && reportTypesWithNoGranularity.has(reportType) ? ChartGrain.NONE : currentReport?.grain ?? ChartGrain.DAILY,
        restFilters,
        metadata && metadata.metrics ? ['Date', ...metadata?.metrics.map((metric) => metric.filterKey)] : undefined,
        adDistributionFilter && adDistributionFilter.length >= 1 ? adDistributionFilter[0] : 'NativeAndPaidSearch'
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportType, dateRange, isMetadataLoading, currentReport?.grain, adDistributionFilter, restFilters]
  );

  // --- Fetch report data ---
  const { data: aggregatedReportData } = usePubCenterReportQuery(
    aggregatedDataRequestBody,
    reportType && reportTypesWithNoChart.has(reportType) ? false : !isMetadataLoading
  );

  const summaryData = useMemo(
    () =>
      reportType && reportTypesWithNoChart.has(reportType)
        ? reportData?.Data?.slice(0, 1)
        : summaryMetricReportData
        ? summaryMetricReportData.Data
        : aggregateReportData(aggregatedReportData?.Data),
    [reportData, summaryMetricReportData, aggregatedReportData, reportType]
  );

  const reportChartData = useMemo(
    () =>
      reportType && reportTypesWithNoChart.has(reportType)
        ? reportData?.Data?.slice(0, 1)
        : aggregatedReportData
        ? aggregateByField(aggregatedReportData.Data, TythonColumnsEnum.Date)
        : undefined,
    [reportType, reportData, aggregatedReportData]
  );

  useEffect(() => {
    setSearchQuery('');
  }, [reportId]);

  // --- Handlers ---

  const handleOnSelectedDatesChanged = (selectedDateRange: ICustomCalenderDateRange, newRangeType: RangeType, grain: ChartGrain) => {
    if (!currentReport) {
      return;
    }
    onReportChange(
      {
        ...currentReport,
        dateRange: selectedDateRange,
        rangeType: newRangeType,
        grain: grain,
      },
      'modified'
    );
  };

  const handleOnColumnsChange = (setterFunction: (prev: TythonColumnsEnum[]) => TythonColumnsEnum[]) => {
    if (!currentReport) {
      return;
    }

    const newColumns = setterFunction(currentReport.columns || []);
    onReportChange({ ...currentReport, columns: newColumns }, 'modified');
  };

  const handleOnFiltersChange = (setterFunction: (prev: { [key: string]: string[] }) => { [key: string]: string[] }) => {
    if (!currentReport) {
      return;
    }

    const newFilters = setterFunction(currentReport.filters || {});
    onReportChange({ ...currentReport, filters: newFilters }, 'filtered');
  };

  const onReportChange = (report: IReportItem, changeType: 'filtered' | 'modified') => {
    if (!currentReport) {
      return;
    }

    if (isPremadeReport) {
      addNewReport(report, changeType);
    } else {
      changeReport({ ...report, isSaved: false });
    }
  };

  const addNewReport = (report: IReportItem, changeType: 'filtered' | 'modified') => {
    const newReportName = generateNewName(
      report.name,
      reports.flatMap((s) => s.items.map((item) => item.name)),
      changeType
    );
    const newReportId = uuidv4();
    onChangeReportId(newReportId, props.history);
    addToMyReports({ ...report, id: newReportId, name: newReportName, isSaved: false });
  };

  const handleOnRename = (newName: string) => {
    if (!currentReport) {
      return;
    }
    changeReport({
      ...currentReport,
      name: generateNewName(
        newName,
        reports.flatMap((s) => s.items.map((item) => item.name)),
        ''
      ),
    });
  };

  const handleOnSave = () => {
    if (!currentReport) {
      return;
    }
    if (isNewReport) {
      postReport({ currentReport, history: props.history });
    } else {
      editReport(currentReport);
    }
  };

  const handleOnSaveAsCopy = () => {
    if (!currentReport) {
      return;
    }
    postReport({ currentReport: { ...currentReport, name: `Copy Of ${currentReport.name}` }, history: props.history });
  };

  const handleOnDeleteSavedReport = () => {
    if (!currentReport) {
      return;
    }

    deleteReport({ reportId: currentReport.id, history: props.history });
  };

  const handleOnSchedule = (enabled: boolean, emails?: string[], frequency?: ChartGrain) => {
    if (!currentReport) {
      return;
    }
    if (isPremadeReport && enabled) {
      postReport({
        currentReport: {
          ...currentReport,
          name: generateNewName(
            currentReport.name,
            reports.flatMap((s) => s.items.map((item) => item.name)),
            'scheduled ' + frequency
          ),
          isScheduled: true,
          reportScheduleFrequency: frequency,
          reportDeliveryEmails: emails,
        },
        history: props.history,
      });
    } else if (!isPremadeReport) {
      editReport({
        ...currentReport,
        isScheduled: enabled,
        reportScheduleFrequency: frequency,
        reportDeliveryEmails: enabled ? emails : [],
      });
    }
  };

  // helpers
  const getLoadingLabel = (): string => {
    if (isPostLoading) {
      return 'Saving Report...';
    }
    if (isDeleteLoading) {
      return 'Deleting Report...';
    }
    if (isEditLoading) {
      return 'Editing Report...';
    }
    return 'Loading';
  };

  return (
    <EmptyLayout>
      <FluentProvider theme={webLightTheme}>
        {!currentReport || isPostLoading || isDeleteLoading || isEditLoading ? (
          <Spinner style={{ top: '50%', left: '50%', position: 'absolute' }} label={getLoadingLabel()} />
        ) : (
          <Stack horizontal>
            <Stack className={classNames.detailPageContainerStyle}>
              <ReportActionComponent
                cardRef={performanceReportCardRef}
                reportData={reportData as PubCenterReportingDataResponse}
                downloadReportName={requestBody.reportName}
                isReportDataLoaded={!isReportDataLoading && reportData !== undefined}
                isReportUnsaved={!currentReport.isSaved}
                isReportPremade={isPremadeReport}
                isNewReport={isNewReport}
                reportName={currentReport.name}
                isReportScheduled={currentReport.isScheduled}
                defaultScheduleFreq={currentReport.reportScheduleFrequency}
                defaultScheduleEmails={currentReport.reportDeliveryEmails}
                isRangeTypeCustom={isRangeTypeCustom}
                handleOnRename={handleOnRename}
                onSave={handleOnSave}
                onSaveAsCopy={handleOnSaveAsCopy}
                onDelete={handleOnDeleteSavedReport}
                onSchedule={handleOnSchedule}
              />
              {reportData?.DataDelay && <ReportDataDelayMessageBar reportName={currentReport.name} />}
              {/* Filter + Data Actions Section */}
              <DataActionComponent
                key={`${reportId}`}
                onSelectedDatesChanged={handleOnSelectedDatesChanged}
                dateRange={dateRange}
                initialDateRangeType={currentReport.rangeType}
                filtersAvailable={filtersAvailable}
                selectedFilters={currentReport.filters}
                setSelectedFilters={handleOnFiltersChange}
                isReportDataLoading={isReportDataLoading}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                granularity={currentReport.grain}
                grains={grains}
                hideChart={hideChart}
                setHideChart={setHideChart}
                reportType={reportType}
                isSnapshotReport={isSnapshotReport}
                isMetadataLoading={isMetadataLoading}
              />
              <div>
                {/* Graph and Table Section*/}
                <PerformanceChartComponent
                  cardRef={performanceReportCardRef}
                  history={props.history}
                  location={props.location}
                  match={props.match}
                  dateRange={dateRange}
                  hideChart={reportType && reportTypesWithNoChart.has(reportType) ? true : hideChart}
                  searchQuery={debouncedSearchQuery}
                  reportData={reportData?.Data}
                  reportDataDate={reportChartData}
                  reportDataSummary={summaryData}
                  reportMetadata={
                    metadata
                      ? {
                          ...metadata,
                          metrics: Array.isArray(metadata.metrics)
                            ? metadata.metrics.filter((m) => m.filterKey !== 'RevenueSharePercent')
                            : metadata.metrics,
                        }
                      : undefined
                  }
                  reportType={reportType ?? 'account'}
                  setDefaultSelectedColumns={handleOnColumnsChange}
                  defaultSelectedColumns={currentReport.columns}
                  chartGrain={currentReport.grain}
                />
              </div>
            </Stack>
          </Stack>
        )}
      </FluentProvider>
    </EmptyLayout>
  );
});
